import React from 'react';
import { Box } from '@mui/material';
import analytics from '../../utils/analytics';
import type { Partner } from '../LeadFormIndex/types';
import type { ErrorTypeType } from './content';
import PlrModal from '../../feature/preloan/v3-redesign/landing/PlrModal';
import { PlrButton, PlrTextLink } from '../../feature/preloan/v3-redesign/landing/ui';
import AgentLink from '../../feature/preloan/v3-redesign/landing/AgentLink';
import { plr } from '../../feature/preloan/v3-redesign/landing/tokens';

// Errores de ClaveÚnica del rediseño: mismos casos, títulos, textos y eventos que
// errorDialog.tsx + content.tsx (flujo anterior), en una hoja del rediseño (PlrModal).

type Errors = {
  isPasswordError: boolean,
  isDataExtractionError: boolean,
  isBlockedPasswordError: boolean,
  isMaintenanceError: boolean,
  isAFCBlockedPasswordError: boolean,
  isNotSubscribedError: boolean,
  isConnectionError: boolean,
  isExternalInformationError: boolean,
};

const errorTypeOf = (errors: Errors): ErrorTypeType => {
  // Mismo orden de prioridad que el diálogo anterior (el último que aplica gana).
  let type: ErrorTypeType = '';
  if (errors.isPasswordError) type = 'passwordError';
  if (errors.isBlockedPasswordError) type = 'blockedPassword';
  if (errors.isMaintenanceError) type = 'maintenanceError';
  if (errors.isAFCBlockedPasswordError) type = 'afcBlockedPassword';
  if (errors.isNotSubscribedError) type = 'notSubscribedError';
  if (errors.isConnectionError) type = 'connectionError';
  if (errors.isDataExtractionError) type = 'dataExtractionError';
  if (errors.isExternalInformationError) type = 'externalInformationError';
  return type;
};

const goToForgotPassword = () => {
  analytics.track('Click Link', { text: '¿Olvidaste tu ClaveÚnica?', initiator: 'usuario' });
  window.open('https://claveunica.gob.cl/recuperar');
};

const goToAFC = () => {
  analytics.track('Click Link', {
    text: 'Administradora de Fondos de Cesantía (AFC)',
    initiator: 'usuario',
  });
  window.open('https://webafiliados.afc.cl/WUI.AAP.OVIRTUAL/Default.aspx');
};

function P({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="p"
      sx={{
        m: 0, fontSize: { xs: 15, sm: 16 }, lineHeight: 1.5, color: plr.body,
      }}
    >
      {children}
    </Box>
  );
}

function Strong({ children }: { children: React.ReactNode }) {
  return <Box component="strong" sx={{ color: plr.ink, fontWeight: 700 }}>{children}</Box>;
}

function body(type: ErrorTypeType, numTries: number, partnerName: string) {
  switch (type) {
    case 'blockedPassword':
      return (
        <>
          <P>
            <Strong>Tu ClaveÚnica se encuentra bloqueada.</Strong>
            {' Recupérala en el sitio de ClaveÚnica y vuelve con nosotros. También tienes la posibilidad de subir tus archivos para analizar tu situación financiera, pero el proceso es más lento.'}
          </P>
          <PlrTextLink onClick={goToForgotPassword} testId="cu-error-recover">
            Recuperar ClaveÚnica
          </PlrTextLink>
        </>
      );
    case 'passwordError':
      return (
        <>
          {numTries > 2 ? (
            <P>
              <Strong>Llevas muchos intentos de ingresar tu ClaveÚnica sin éxito.</Strong>
              {' Si la quieres recuperar, entra a "¿Olvidaste tu ClaveÚnica?" y vuelve con nosotros.'}
            </P>
          ) : (
            <P>
              <Strong>Has ingresado de manera incorrecta tu contraseña de ClaveÚnica</Strong>
              {', por lo que no pudimos consultar tus datos. Si ingresas incorrectamente tu contraseña reiteradas veces, '}
              <Strong>es posible que se te bloquee.</Strong>
            </P>
          )}
          <Box
            data-pw="cu-error-tries"
            sx={{ fontSize: { xs: 14, sm: 15 }, fontWeight: 700, color: plr.danger }}
          >
            {`Intentos: ${numTries} de 2`}
          </Box>
          <PlrTextLink onClick={goToForgotPassword} testId="cu-error-forgot">
            ¿Olvidaste tu ClaveÚnica?
          </PlrTextLink>
        </>
      );
    case 'dataExtractionError':
      return (
        <>
          <P>
            No hemos podido procesar tu evaluación debido a una caída en alguna de las fuentes
            de información que consultamos.
          </P>
          <P>
            Debes volver a intentar cuando dicha fuente vuelva a estar disponible, ya que
            lamentablemente es un servicio que no depende de banca.me.
          </P>
        </>
      );
    case 'maintenanceError':
      return (
        <>
          <P>
            <Strong>Los servicios consultados se encuentran en mantención.</Strong>
            {' Por favor vuelve más tarde para que podamos evaluarte oportunamente.'}
          </P>
          <P>¡Disculpa las molestias!</P>
        </>
      );
    case 'afcBlockedPassword':
      return (
        <>
          <P>
            <Strong>
              Hay un problema con tu contraseña en la AFC (Administradora de Fondos de
              Cesantía), debido a que está bloqueada o aún no la has creado.
            </Strong>
            {' Puedes revisar tu situación ingresando con tu ClaveÚnica.'}
          </P>
          <PlrTextLink onClick={goToAFC} testId="cu-error-afc">
            Administradora de Fondos de Cesantía (AFC)
          </PlrTextLink>
          <P>¡Vuelve con nosotros cuando hayas recuperado o creado tu contraseña!</P>
        </>
      );
    case 'notSubscribedError':
      return (
        <>
          <P>
            <Strong>No apareces inscrito en la Administradora de Fondos de Cesantía (AFC).</Strong>
            {' Puedes revisar tu situación en su sitio.'}
          </P>
          <PlrTextLink onClick={goToAFC} testId="cu-error-afc">
            Administradora de Fondos de Cesantía (AFC)
          </PlrTextLink>
          <P>
            <Strong>Atención:</Strong>
            {' si eres funcionario de alguna entidad pública (Carabineros, Municipalidades, Ejército, Hospitales, entre otros), por el momento no podremos atenderte debido a que no tenemos convenio con esas instituciones.'}
          </P>
          <P>
            Si estás recién comenzando a trabajar con contrato, la AFC normalmente demora en
            actualizarse. Si piensas que esto es un error, contáctanos.
          </P>
          {partnerName === 'Banca.Me' && <AgentLink />}
        </>
      );
    case 'connectionError':
      return (
        <P>
          <Strong>Hubo un error en la conexión de nuestra aplicación con tu ClaveÚnica</Strong>
          , por lo que no pudimos consultar tus datos. Inténtalo de nuevo para que podamos
          evaluar tu situación oportunamente. Si falla varias veces, te pediremos que vuelvas
          en otro momento o ingreses tus datos manualmente.
        </P>
      );
    case 'externalInformationError':
      return (
        <>
          <P>
            <Strong>
              No hemos podido procesar tu evaluación porque uno de nuestros proveedores de
              información está temporalmente fuera de servicio.
            </Strong>
            {' Por favor vuelve más tarde para que podamos evaluarte oportunamente.'}
          </P>
          <P>¡Disculpa las molestias!</P>
        </>
      );
    default:
      return null;
  }
}

export default function ClaveUnicaErrorSheet(props: {
  errors: Errors,
  numTries: number,
  retryCUSubmit: () => void,
  partner: Partner,
}) {
  const {
    errors, numTries, retryCUSubmit, partner,
  } = props;
  const [open, setOpen] = React.useState(false);
  const [type, setType] = React.useState<ErrorTypeType>('');

  React.useEffect(() => {
    if (Object.values(errors).some(Boolean)) {
      setType(errorTypeOf(errors));
      setOpen(true);
    }
  }, [errors]);

  let title = numTries > 2 ? 'Falta información' : 'Volver a ingresar ClaveÚnica';
  if (type === 'dataExtractionError') title = 'Vuelve más tarde';

  const close = () => setOpen(false);
  const retry = () => {
    analytics.track('Click boton', { text: 'Reintentar', initiator: 'usuario' });
    close();
    retryCUSubmit();
  };

  return (
    <PlrModal
      open={open}
      onClose={close}
      title={title}
      titleId="cu-error-title"
      testId="cu-error-dialog"
      width={480}
    >
      {body(type, numTries, partner.partnerName)}
      <Box sx={{
        display: 'flex', flexDirection: 'column', gap: '10px', mt: '4px',
      }}
      >
        {type === 'connectionError' && (
          <PlrButton testId="cu-error-retry" onClick={retry}>Reintentar</PlrButton>
        )}
        <PlrButton
          testId="cu-error-ok"
          variant={type === 'connectionError' ? 'outlined' : 'contained'}
          onClick={close}
        >
          Entendido
        </PlrButton>
      </Box>
    </PlrModal>
  );
}
